import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, RefreshCw } from "lucide-react";
import { Toaster } from "sonner";
import {
  useCapabilityOverrides,
  useCapabilityRegistry,
} from "@/hooks/useCapabilities";
import { CapabilityCombobox } from "@/components/capabilities/CapabilityCombobox";
import { CapabilityOverrideFormDialog } from "@/components/capabilities/CapabilityOverrideFormDialog";
import { DeleteCapabilityOverrideDialog } from "@/components/capabilities/DeleteCapabilityOverrideDialog";
import { BulkCapabilityDecisionDialog } from "@/components/capabilities/BulkCapabilityDecisionDialog";
import { CapabilityOverridesTable } from "@/components/capabilities/CapabilityOverridesTable";
import { CapabilityExplainPanel } from "@/components/capabilities/CapabilityExplainPanel";
import { OrphansExpiredPanel } from "@/components/capabilities/OrphansExpiredPanel";
import { useUrlState } from "@/hooks/useUrlState";
import { useSessionState } from "@/hooks/useSessionState";
import { serializeSet, deserializeSet } from "@/lib/stateUtils";
import type {
  CapabilitiesUrlState,
  CapabilitiesSessionState,
} from "@/types/pageState";
import type { CapabilityScopeType } from "@/services/capabilitiesApi";

export default function Capabilities() {
  const [urlState, setUrlState] = useUrlState<CapabilitiesUrlState>({
    schema: {
      tab: "string",
      capability: "string",
      scope_type: "string",
      scope_id: "string",
    },
    defaults: { tab: "overrides" },
    replace: true,
    debounceMs: 300,
  });

  const [sessionState, setSessionState] =
    useSessionState<CapabilitiesSessionState>({
      key: "capabilities-ui",
      defaultValue: {
        createDialogOpen: false,
        deleteOverrideId: null,
        selectedOverrideIds: new Set(),
        bulkDecision: null,
      },
      serialize: (state) =>
        JSON.stringify({
          ...state,
          selectedOverrideIds: serializeSet(state.selectedOverrideIds),
        }),
      deserialize: (str) => {
        const parsed = JSON.parse(str);
        return {
          ...parsed,
          selectedOverrideIds: deserializeSet(parsed.selectedOverrideIds),
          bulkDecision: parsed.bulkDecision ?? null,
        };
      },
    });

  const activeTab = urlState.tab ?? "overrides";
  const capabilityFilter = urlState.capability;
  const scopeTypeFilter = urlState.scope_type as
    | CapabilityScopeType
    | undefined;
  const scopeIdFilter = urlState.scope_id;

  const overridesQuery = useCapabilityOverrides(capabilityFilter);
  const { data: registry = [], isLoading: registryLoading } =
    useCapabilityRegistry();

  const overrides = useMemo(() => {
    let rows = overridesQuery.data ?? [];
    if (scopeTypeFilter) {
      rows = rows.filter((row) => row.scope_type === scopeTypeFilter);
    }
    if (
      scopeIdFilter &&
      (scopeTypeFilter === "source" || scopeTypeFilter === "user")
    ) {
      const q = scopeIdFilter.trim().toLowerCase();
      if (q) {
        rows = rows.filter((row) => row.scope_id.toLowerCase().includes(q));
      }
    }
    return rows;
  }, [overridesQuery.data, scopeTypeFilter, scopeIdFilter]);

  const selectedOverrides = useMemo(
    () =>
      overrides.filter((row) => sessionState.selectedOverrideIds.has(row.id)),
    [overrides, sessionState.selectedOverrideIds],
  );

  const deleteOverride =
    overrides.find((o) => o.id === sessionState.deleteOverrideId) ?? null;

  const hasFilters =
    !!urlState.capability || !!urlState.scope_type || !!urlState.scope_id;

  return (
    <div className="space-y-6 p-6">
      <Toaster />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Capabilities</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Write and explain global, source, and user-scoped overrides on top
            of the capability registry. Empty at rest — every row here is a
            deliberate deviation from the default.
          </p>
        </div>
        {activeTab === "overrides" && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => overridesQuery.refetch()}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setSessionState((prev) => ({ ...prev, createDialogOpen: true }))
              }
            >
              <Plus className="mr-1 h-4 w-4" />
              New Override
            </Button>
          </div>
        )}
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) =>
          setUrlState((prev) => ({
            ...prev,
            tab: value as CapabilitiesUrlState["tab"],
          }))
        }
      >
        <TabsList>
          <TabsTrigger value="overrides">Overrides</TabsTrigger>
          <TabsTrigger value="explain">Explain</TabsTrigger>
          <TabsTrigger value="orphans">Orphans &amp; Expired</TabsTrigger>
        </TabsList>

        <TabsContent value="overrides" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Capability
              </label>
              <CapabilityCombobox
                capabilities={registry}
                isLoading={registryLoading}
                value={urlState.capability}
                onValueChange={(capability) => {
                  setUrlState({
                    capability: capability || undefined,
                  });
                }}
                className="w-64"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Scope
              </label>
              <Select
                value={urlState.scope_type || "all"}
                onValueChange={(v) => {
                  if (v === "all") {
                    setUrlState({
                      scope_type: undefined,
                      scope_id: undefined,
                    });
                    return;
                  }
                  setUrlState({
                    scope_type: v,
                    scope_id: undefined,
                  });
                }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="global">Global</SelectItem>
                  <SelectItem value="source">Source</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(scopeTypeFilter === "source" || scopeTypeFilter === "user") && (
              <div>
                <label
                  htmlFor="capabilities-scope-id"
                  className="mb-1 block text-sm text-muted-foreground"
                >
                  {scopeTypeFilter === "source" ? "Source" : "User"}
                </label>
                <Input
                  id="capabilities-scope-id"
                  value={urlState.scope_id ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setUrlState({
                      scope_id: value.trim() ? value : undefined,
                    });
                  }}
                  placeholder={
                    scopeTypeFilter === "source"
                      ? "Filter by source name..."
                      : "Filter by username or id..."
                  }
                  className="w-56"
                />
              </div>
            )}

            {hasFilters && (
              <div className="self-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setUrlState({
                      capability: undefined,
                      scope_type: undefined,
                      scope_id: undefined,
                    })
                  }
                >
                  Clear filters
                </Button>
              </div>
            )}
          </div>

          <CapabilityOverridesTable
            overrides={overrides}
            isLoading={overridesQuery.isLoading}
            selectedIds={sessionState.selectedOverrideIds}
            onSelectedIdsChange={(ids) =>
              setSessionState((prev) => ({
                ...prev,
                selectedOverrideIds: ids,
              }))
            }
            onDelete={(override) =>
              setSessionState((prev) => ({
                ...prev,
                deleteOverrideId: override.id,
              }))
            }
            onBulkDecision={(decision) =>
              setSessionState((prev) => ({ ...prev, bulkDecision: decision }))
            }
          />

          {!overridesQuery.isLoading && overrides.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Showing {overrides.length} override
              {overrides.length === 1 ? "" : "s"}
              {hasFilters && " (filtered)"}
            </p>
          )}
        </TabsContent>

        <TabsContent value="explain">
          <CapabilityExplainPanel initialCapability={capabilityFilter} />
        </TabsContent>

        <TabsContent value="orphans">
          <OrphansExpiredPanel />
        </TabsContent>
      </Tabs>

      <CapabilityOverrideFormDialog
        open={sessionState.createDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, createDialogOpen: open }))
        }
      />

      <DeleteCapabilityOverrideDialog
        open={sessionState.deleteOverrideId != null}
        onOpenChange={(open) => {
          if (!open)
            setSessionState((prev) => ({ ...prev, deleteOverrideId: null }));
        }}
        override={deleteOverride}
      />

      <BulkCapabilityDecisionDialog
        open={sessionState.bulkDecision != null}
        onOpenChange={(open) => {
          if (!open)
            setSessionState((prev) => ({ ...prev, bulkDecision: null }));
        }}
        decision={sessionState.bulkDecision}
        overrides={selectedOverrides}
        onComplete={(succeededIds) =>
          setSessionState((prev) => {
            const next = new Set(prev.selectedOverrideIds);
            succeededIds.forEach((id) => next.delete(id));
            return { ...prev, selectedOverrideIds: next, bulkDecision: null };
          })
        }
      />
    </div>
  );
}
